import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { crmTypes } from 'utils/helpers/crm';
import { Modal } from 'antd';
import { v7 as uuidv7 } from 'uuid';

import Summary from 'components/Export/Summary';
import { downloadType } from 'components/Export/Summary/constants';
import { useSelector } from 'react-redux';
import {
  getActiveList,
  getAreAllMembersSelected,
  getSelectedMemberIds,
  getSelectedMembers,
} from 'selectors/lists';
import styled from 'styled-components';
import { useGet, usePost } from 'hooks';
import { getCRMLists, pushMembers } from 'services/api';
import { ListCRMLink } from 'components/common';
import { handleFailedExportNotification } from 'components/Search/Results/Actions/notifications';
import Footer from './PushToCrmFooter';
import useExport from '../Actions/useExport';
import { views } from '../constants';

const InnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  .export-summary {
    width: auto;
    .exclude-previous-checkbox .ant-checkbox + span {
      font-size: 14px;
    }
  }
`;

function PushToCrm({ crmType, onClose, fullList }) {
  const [{ res: crmLists, error: getCrmListsError }] = useGet({
    url: getCRMLists,
    query: { crmType },
    onMount: true,
  });
  const crmOptions = useMemo(
    () =>
      getCrmListsError
        ? []
        : crmLists?.lists?.map(o => ({ label: o.name, value: o.crmId })) || [],
    [crmLists?.lists, getCrmListsError]
  );

  const selectedMemberIds = useSelector(getSelectedMemberIds);
  const selectedMembers = useSelector(getSelectedMembers);
  const allSelected = useSelector(getAreAllMembersSelected);
  const [crmId, setCrmId] = useState(null);

  const transcactionId = useMemo(() => uuidv7(), []);
  const activeList = useSelector(getActiveList);
  const [{ res, loading: pushLoading, error }, postPush] = usePost({
    url: pushMembers(activeList.id),
  });

  useEffect(() => {
    if (res) {
      onClose();
    }
  }, [onClose, res, fullList]);

  useEffect(() => {
    if (error) {
      if (error.status === 412) {
        onClose();
      }
      handleFailedExportNotification(error.status);
    }
  }, [error, onClose]);

  const [getExportQueryBody] = useExport(views.researchers, fullList);

  const handleExport = params => {
    if (fullList || allSelected) {
      postPush({ crmId, crmType, ...(params ?? {}) });
      return;
    }

    if (selectedMembers.all) {
      postPush({
        crmId,
        crmType,
        excludedFromAllResearcherIds: Object.keys(selectedMembers.exclude),
        ...(params ?? {}),
      });
    } else {
      postPush({
        crmId,
        crmType,
        researcherIds: selectedMemberIds,
        ...(params ?? {}),
      });
    }
  };

  return (
    <Modal
      open
      title={`Push to ${crmType}`}
      onCancel={onClose}
      footer={null}
      okText={`Push to ${crmType}`}
    >
      <InnerContainer>
        <Summary
          transactionId={transcactionId}
          regenerateTransactionId={() => uuidv7()}
          downloadType={downloadType.crmPush}
          onClose={e => {
            if (e) {
              onClose();
            }
          }}
          getQueryBody={getExportQueryBody}
          onExport={handleExport}
          canUseCredits
          className='export-summary'
          crmLists={
            <ListCRMLink
              label={`Target ${crmType} campaign/list`}
              value={crmId}
              onChange={value => setCrmId(value)}
              options={crmOptions}
              style={{ marginTop: 0 }}
            />
          }
          footer={props => (
            <Footer
              crmId={crmId}
              loading={pushLoading}
              crmType={crmType}
              {...props}
            />
          )}
          isExporting={pushLoading}
        />
      </InnerContainer>
    </Modal>
  );
}

PushToCrm.propTypes = {
  crmType: PropTypes.oneOf(Object.values(crmTypes)),
  onClose: PropTypes.func.isRequired,
  fullList: PropTypes.bool,
};

export default PushToCrm;
